import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useFilter } from "../../context/FilterContext";
import { formatSalesValue } from './../../utils/formatUtils';
import { formatQuantity } from "../../utils/quantityConversion";

const AddOpeningCount = () => {
    const today = new Date().toLocaleDateString('en-CA');
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const inputRefs = useRef([]);
    const groupRefs = useRef({});
    const itemRefs = useRef({});

    const navigate = useNavigate();
    const location = useLocation();
    const {
        inventoryData,
        selectedOutlet,
        accessToken,
        food_start_date,
        beverage_start_date
    } = location.state || {};

    const formatToISODate = (dateString, addDays = 0) => {
        if (!dateString) return "";

        const date = new Date(dateString);
        date.setDate(date.getDate() + addDays);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const { filter, setFilter } = useFilter();

    const getOpeningDate = () => {
        if (filter === "food") return food_start_date;
        if (filter === "beverage") return beverage_start_date;
        return null;
    };

    const [editableData, setEditableData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [startDate, setStartDate] = useState(() => {
        const opening = getOpeningDate();
        return opening ? formatToISODate(opening) : today;
    });

    // ── Search state ─────────────────────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);

    useEffect(() => {
        const opening = getOpeningDate();
        if (opening) {
            setStartDate(formatToISODate(opening, 1));
        } else {
            setStartDate(today);
        }
    }, [filter, food_start_date, beverage_start_date]);

    useEffect(() => {
        if (accessToken !== "a7fK2pX9Zd") {
            navigate('/inventorycalculation');
        }
    }, [accessToken, navigate]);

    const getEndpoint = (base) => {
        return `${base}-${filter?.toLowerCase() || "food"}`;
    };

    const api = {
        save: getEndpoint('save-inventory-snapshot')
    };

    const unitMappings = {
        kgVariations: ['kg', 'kgs', 'kilogram', 'kilograms'],
        gramVariations: ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'],
        literVariations: ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'],
        milliliterVariations: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls']
    };

    const handleDateChange = (e, type) => {
        const value = e.target.value;
        if (type === 'start') {
            setStartDate(value);
        }
    };

    useEffect(() => {
        if (inventoryData?.summary) {
            const flattenedData = Object.entries(inventoryData.summary)
                .flatMap(([category, groups]) =>
                    Object.entries(groups)
                        .flatMap(([groupName, items]) =>
                            items.map(item => {
                                const hasOpening = (parseFloat(item.opening) || 0) > 0;
                                const { primaryQty, secondaryQty } = hasOpening
                                    ? reverseComputeQuantities(item)
                                    : { primaryQty: "", secondaryQty: "" };

                                return {
                                    ...item,
                                    _key: `${category}-${groupName}-${item.name}`,
                                    category,
                                    groupName,
                                    editableOpening: item.opening,
                                    primaryQty,
                                    secondaryQty,
                                    unitdefinition: item.unitdefinition,
                                    opening_rate: item.opening_rate
                                };
                            })
                        ));
            setEditableData(flattenedData);
        }
    }, [inventoryData]);

    const filteredData = editableData.filter(
        item => item.category?.toLowerCase() === filter
    );

    // Group filtered data by groupName
    const groupedData = filteredData.reduce((acc, item) => {
        const group = item.groupName || "Uncategorized";
        if (!acc[group]) acc[group] = [];
        acc[group].push(item);
        return acc;
    }, {});

    // ── Build a flat searchable list whenever groupedData changes ──────────────
    const searchableEntries = useMemo(() => {
        const entries = [];

        Object.entries(groupedData).forEach(([groupName, items]) => {
            entries.push({ type: "group", label: groupName, groupName });

            items.forEach((item) => {
                entries.push({
                    type: "item",
                    label: item.name,
                    groupName,
                    itemKey: item._key,
                });
            });
        });

        return entries;
    }, [groupedData]);

    // ── Run search on query change ───────────────────────────────────────────
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (!query.trim()) {
            setSearchResults([]);
            setShowSearchDropdown(false);
            return;
        }

        const lowerQuery = query.toLowerCase();
        const matches = searchableEntries.filter((entry) =>
            entry.label.toLowerCase().includes(lowerQuery)
        );

        setSearchResults(matches.slice(0, 15));
        setShowSearchDropdown(true);
    };

    // ── Scroll to a group or a specific item row ─────────────────────────────
    const handleSelectSearchResult = (entry) => {
        setShowSearchDropdown(false);
        setSearchQuery(entry.label);

        if (entry.type === "group") {
            const el = groupRefs.current[entry.groupName];
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            return;
        }

        const groupEl = groupRefs.current[entry.groupName];
        if (groupEl) {
            groupEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        setTimeout(() => {
            const rowEl = itemRefs.current[entry.itemKey];
            if (rowEl) {
                rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
                rowEl.classList.add("bg-blue-100");
                setTimeout(() => rowEl.classList.remove("bg-blue-100"), 1500);
            }
        }, 300);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter" && searchResults.length > 0) {
            handleSelectSearchResult(searchResults[0]);
        } else if (e.key === "Escape") {
            setShowSearchDropdown(false);
        }
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSearchResults([]);
        setShowSearchDropdown(false);
    };

    const handlePrimaryChange = (itemKey, value) => {
        setEditableData(prev =>
            prev.map(item =>
                item._key === itemKey
                    ? {
                        ...item,
                        primaryQty: value,
                        editableOpening: computeFinalQuantity({
                            ...item,
                            primaryQty: value
                        })
                    }
                    : item
            )
        );
    };

    const handleSecondaryChange = (itemKey, value) => {
        setEditableData(prev =>
            prev.map(item =>
                item._key === itemKey
                    ? {
                        ...item,
                        secondaryQty: value,
                        editableOpening: computeFinalQuantity({
                            ...item,
                            secondaryQty: value
                        })
                    }
                    : item
            )
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const currentIndex = inputRefs.current.indexOf(e.target);
            const nextInput = inputRefs.current[currentIndex + 1];
            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    function computeFinalQuantity(item) {
        const primaryQty = parseFloat(item.primaryQty) || 0;
        const secondaryQty = parseFloat(item.secondaryQty) || 0;
        const unitSize = parseFloat(item.unitdefinition?.unit) || 1;
        const baseUom = (item.unitdefinition?.uom || '').toLowerCase();
        const itemUom = (item.primary_unit || '').toLowerCase();
        const newUom = (item.secondary_unit || '').toLowerCase();

        const { kgVariations, gramVariations, literVariations, milliliterVariations } = unitMappings;
        const isMatch = (uom, variations) => variations.includes(uom);

        let primaryInBaseUnit, secondaryInBaseUnit;

        if (itemUom === baseUom) {
            primaryInBaseUnit = primaryQty;
        } else {
            primaryInBaseUnit = primaryQty * unitSize;
        }

        if (newUom === baseUom) {
            secondaryInBaseUnit = secondaryQty;
        } else {
            if (isMatch(baseUom, kgVariations) && isMatch(newUom, gramVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else if (isMatch(baseUom, literVariations) && isMatch(newUom, milliliterVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else {
                secondaryInBaseUnit = secondaryQty;
            }
        }

        const totalInBaseUnit = (primaryInBaseUnit + secondaryInBaseUnit) / unitSize;
        return totalInBaseUnit;
    }

    function reverseComputeQuantities(item) {
        const opening = parseFloat(item.opening) || 0;
        if (!opening) return { primaryQty: "", secondaryQty: "" };

        const unitSize = parseFloat(item.unitdefinition?.unit) || 1;
        const baseUom = (item.unitdefinition?.uom || '').toLowerCase();
        const itemUom = (item.primary_unit || '').toLowerCase();
        const newUom = (item.secondary_unit || '').toLowerCase();

        const { kgVariations, gramVariations, literVariations, milliliterVariations } = unitMappings;
        const isMatch = (uom, variations) => variations.includes(uom);

        const primaryMultiplier = itemUom === baseUom ? 1 : unitSize;
        const totalInBaseUnit = opening * unitSize;

        // No secondary input shown for this item -> dump the whole value into primary, don't floor it
        if (!shouldShowSecondary(item)) {
            const primaryQty = totalInBaseUnit / primaryMultiplier;
            return {
                primaryQty: primaryQty ? String(parseFloat(primaryQty.toFixed(4))) : "",
                secondaryQty: ""
            };
        }

        let secondaryMultiplier;
        if (newUom === baseUom) {
            secondaryMultiplier = 1;
        } else if (isMatch(baseUom, kgVariations) && isMatch(newUom, gramVariations)) {
            secondaryMultiplier = 1 / 1000;
        } else if (isMatch(baseUom, literVariations) && isMatch(newUom, milliliterVariations)) {
            secondaryMultiplier = 1 / 1000;
        } else {
            secondaryMultiplier = 1;
        }

        const primaryQty = Math.floor(totalInBaseUnit / primaryMultiplier);
        const remainder = totalInBaseUnit - (primaryQty * primaryMultiplier);
        const secondaryQty = remainder / secondaryMultiplier;

        return {
            primaryQty: primaryQty ? String(primaryQty) : "",
            secondaryQty: secondaryQty ? String(parseFloat(secondaryQty.toFixed(4))) : ""
        };
    }

    const shouldShowSecondary = (item) => {
        const primaryUnit = (item.primary_unit || '').toLowerCase().trim();
        const secondaryUnit = (item.secondary_unit || '').toLowerCase().trim();
        const defUom = (item.unitdefinition?.uom || '').toLowerCase().trim();

        if (!defUom) {
            return primaryUnit !== secondaryUnit;
        }

        return !(primaryUnit === secondaryUnit && secondaryUnit === defUom);
    };

    const handleSave = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save inventory data?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!filteredData.length) {
                                toast.error("No items to save for selected category");
                                return;
                            }

                            setIsSaving(true);
                            try {
                                const selected = new Date(startDate);
                                selected.setDate(selected.getDate() - 1);
                                const previousDate = selected.toLocaleDateString('en-CA');

                                const payload = {
                                    token: localStorage.getItem('token'),
                                    outlet: selectedOutlet,
                                    start_date: previousDate,
                                    end_date: previousDate,
                                    physical_added: false,
                                    is_opening: true,
                                    items: filteredData.map(item => ({
                                        name: item.name,
                                        type: item.category,
                                        group_name: item.groupName,
                                        uom: item.uom,
                                        new_uom: item.new_uom,
                                        opening_count: 0,
                                        total_received: 0,
                                        total_consumed: 0,
                                        closing_balance: Number((parseFloat(item.editableOpening) || 0).toFixed(12)),
                                        physical_count: Number((parseFloat(item.editableOpening) || 0).toFixed(12)),
                                    }))
                                };

                                await axios.post(`${baseUrl}/${api.save}`, payload);
                                toast.success('Opening counts saved successfully!');
                                navigate('/inventorycalculation');
                            } catch (error) {
                                console.error('Error saving opening counts:', error);
                                toast.error(error?.response?.data?.error || 'Failed to save opening counts. Please try again.');
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-opening-count'
            }
        );
    };

    const computeCost = (item) => {
        const qty = computeFinalQuantity(item);
        const rate = parseFloat(item.opening_rate) || 0;
        return qty * rate;
    };

    // Shared table header rows
    const TableHeader = () => (
        <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Definition</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Primary Unit</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Secondary Unit</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
            </tr>
            <tr>
                <th></th>
                <th></th>
                <th></th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Qty</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">UOM</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Qty</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">UOM</th>
                <th></th>
            </tr>
        </thead>
    );

    const handlePrint = () => {
        const printWindow = window.open("", "_blank");

        const printGrouped = filteredData.reduce((acc, item) => {
            const group = item.groupName || "Uncategorized";

            if (!acc[group]) acc[group] = [];

            acc[group].push(item);
            return acc;
        }, {});

        // Sort items within each group
        Object.keys(printGrouped).forEach((group) => {
            printGrouped[group].sort((a, b) =>
                (a.name || "")
                    .toLowerCase()
                    .localeCompare((b.name || "").toLowerCase())
            );
        });

        const groupTablesHtml = Object.entries(printGrouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupName, items]) => {
                const isSmallGroup = items.length <= 5;

                return `
                <div class="group-section ${isSmallGroup ? "keep-together" : ""}">
                    <div class="group-header">
                        <h3 class="group-title">${groupName}</h3>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th>Item Name</th>
                                <th>Unit Definition</th>
                                <th>Primary</th>
                                <th>Secondary</th>
                            </tr>
                        </thead>

                        <tbody>
                            ${items
                        .map((item) => {
                            const showSecondary =
                                item.primary_unit !== item.secondary_unit;

                            return `
                                <tr>
                                    <td>${item.name}</td>

                                    <td>
                                        ${item.unitdefinition?.unit || ""}
                                        (${item.unitdefinition?.uom || ""})
                                    </td>

                                    <td>
                                        <div class="qty-box">
                                            <span class="line"></span>
                                            <span>${item.primary_unit || ""}</span>
                                        </div>
                                    </td>

                                    <td>
                                        ${showSecondary
                                    ? `
                                        <div class="qty-box">
                                            <span class="line"></span>
                                            <span>${item.secondary_unit || ""}</span>
                                        </div>
                                    `
                                    : "-"
                                }
                                    </td>
                                </tr>
                            `;
                        })
                        .join("")}
                        </tbody>
                    </table>
                </div>
            `;
            })
            .join("");

        const content = `
        <html>
        <head>
            <title>Opening Stock Count Sheet</title>

            <style>
                body {
                    font-family: Arial, sans-serif;
                    padding: 5px;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 15px;
                    font-size: 14px;
                }

                .group-section {
                    margin-bottom: 24px;
                }

                .group-header {
                    page-break-after: avoid;
                    break-after: avoid;
                }

                .group-title {
                    font-size: 14px;
                    font-weight: bold;
                    margin-bottom: 6px;
                    padding: 4px 8px;
                    background-color: #e8e8e8;
                    border-left: 4px solid #555;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }

                th,
                td {
                    border: 1px solid black;
                    padding: 8px;
                    text-align: center;
                }

                th {
                    background-color: #f2f2f2;
                }

                .qty-box {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 5px;
                }

                .line {
                    width: 60px;
                    border-bottom: 1px solid #000;
                    display: inline-block;
                }

                @media print {
                    /* Small groups stay together */
                    .keep-together {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    /* Keep category title attached to the start of its table */
                    .group-header {
                        page-break-after: avoid;
                        break-after: avoid;
                    }

                    /* Repeat table headers on new pages */
                    thead {
                        display: table-header-group;
                    }

                    tfoot {
                        display: table-footer-group;
                    }

                    table {
                        page-break-inside: auto;
                    }

                    tr {
                        page-break-inside: avoid;
                    }
                }
            </style>
        </head>

        <body>
            <div class="header">
                <div>Opening Stock Count for: ${filter?.toUpperCase()}</div>
                <div>Date: ____________</div>
            </div>

            ${groupTablesHtml}
        </body>
        </html>
    `;

        printWindow.document.write(content);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    const handlePrintOpening = () => {
        const printWindow = window.open("", "_blank");

        const printGrouped = filteredData.reduce((acc, item) => {
            const group = item.groupName || "Uncategorized";
            if (!acc[group]) acc[group] = [];
            acc[group].push(item);
            return acc;
        }, {});

        Object.keys(printGrouped).forEach((group) => {
            printGrouped[group].sort((a, b) =>
                (a.name || "")
                    .toLowerCase()
                    .localeCompare((b.name || "").toLowerCase())
            );
        });

        let grandTotal = 0;
        let totalItemCount = 0;

        const groupTablesHtml = Object.entries(printGrouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupName, items]) => {
                const isSmallGroup = items.length <= 5;
                let groupSubtotal = 0;
                let groupItemCount = 0;

                const rowsHtml = items
                    .map((item) => {
                        const qty = parseFloat(item.opening) || 0;
                        const rate = parseFloat(item.opening_rate) || 0;
                        const total = qty * rate;

                        groupSubtotal += total;
                        groupItemCount += 1;
                        grandTotal += total;
                        totalItemCount += 1;

                        return `
                    <tr>
                        <td style="text-align:left;">${item.name}</td>
                        <td>
                            ${item.unitdefinition?.unit || ""}
                            (${item.unitdefinition?.uom || ""})
                        </td>
                        <td>${formatQuantity(item, qty)}</td>
                        <td>${rate ? formatSalesValue(rate) : "-"}</td>
                        <td>${total ? formatSalesValue(total) : "-"}</td>
                    </tr>
                `;
                    })
                    .join("");

                return `
                    <div class="group-section ${isSmallGroup ? "keep-together" : ""}">
                        <div class="group-header">
                            <h3 class="group-title">${groupName}</h3>
                        </div>

                        <table>
                            <thead>
                                <tr>
                                    <th>Item Name</th>
                                    <th>Unit Definition</th>
                                    <th>Opening Qty</th>
                                    <th>Rate</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${rowsHtml}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colspan="2" style="text-align:left; font-weight:bold;">Items: ${groupItemCount}</td>
                                    <td colspan="2" style="text-align:right; font-weight:bold;">Group Subtotal</td>
                                    <td style="font-weight:bold;">${formatSalesValue(groupSubtotal)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                `;
            })
            .join("");

        const content = `
            <html>
            <head>
                <title>Opening Stock Valuation Sheet</title>

                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 5px;
                    }

                    .header {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 15px;
                        font-size: 14px;
                    }

                    .group-section {
                        margin-bottom: 24px;
                    }

                    .group-header {
                        page-break-after: avoid;
                        break-after: avoid;
                    }

                    .group-title {
                        font-size: 14px;
                        font-weight: bold;
                        margin-bottom: 6px;
                        padding: 4px 8px;
                        background-color: #e8e8e8;
                        border-left: 4px solid #555;
                    }

                    table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 12px;
                    }

                    th,
                    td {
                        border: 1px solid black;
                        padding: 8px;
                        text-align: center;
                    }

                    th {
                        background-color: #f2f2f2;
                    }

                    tfoot td {
                        border-top: 2px solid #000;
                        background-color: #f9f9f9;
                    }

                    .summary {
                        margin-top: 20px;
                        font-size: 14px;
                        display: flex;
                        justify-content: space-between;
                        border-top: 2px solid #000;
                        padding-top: 10px;
                    }

                    .grand-total {
                        font-weight: bold;
                        font-size: 16px;
                    }

                    @media print {
                        .keep-together {
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }

                        .group-header {
                            page-break-after: avoid;
                            break-after: avoid;
                        }

                        thead {
                            display: table-header-group;
                        }

                        tfoot {
                            display: table-footer-group;
                        }

                        table {
                            page-break-inside: auto;
                        }

                        tr {
                            page-break-inside: avoid;
                        }
                    }
                </style>
            </head>

            <body>
                <div class="header">
                    <div>Opening Stock Valuation for: ${filter?.toUpperCase()}</div>
                    <div>Date: ${startDate}</div>
                </div>

                ${groupTablesHtml}

                <div class="summary">
                    <div>Total Items: ${totalItemCount}</div>
                    <div class="grand-total">Grand Total: ${formatSalesValue(grandTotal)}</div>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(content);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="mx-auto max-w-6xl p-3">
                    <div className="text-lg font-semibold mb-1">Opening Count for {selectedOutlet}</div>

                    <div className="flex justify-between items-center mb-2.5">
                        <div className="flex gap-6">
                            <input
                                type="date"
                                className="border p-1 rounded"
                                value={startDate}
                                max={getOpeningDate()
                                    ? formatToISODate(getOpeningDate(), 1)
                                    : today
                                }
                                min={filter === "food"
                                    ? formatToISODate(food_start_date, 1)
                                    : formatToISODate(beverage_start_date, 1)
                                }
                                onChange={(e) => handleDateChange(e, "start")}
                            />

                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    value="food"
                                    checked={filter === "food"}
                                    onChange={(e) => setFilter(e.target.value)}
                                />
                                Food
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    value="beverage"
                                    checked={filter === "beverage"}
                                    onChange={(e) => setFilter(e.target.value)}
                                />
                                Beverage
                            </label>

                            <div className="rounded-md border italic border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                                <span className="font-medium">Note:</span> Only one type can be saved at a time.
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={handlePrint}
                                className="px-3 py-2 rounded-md text-xs font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md"
                            >
                                Print Sheet
                            </button>

                            <button
                                onClick={handlePrintOpening}
                                className="px-3 py-2 rounded-md text-xs font-medium bg-sky-100 text-sky-800 hover:bg-sky-200 hover:shadow-md"
                            >
                                Print Opening
                            </button>
                        </div>
                    </div>

                    {/* ── Search bar ────────────────────────────────────────────── */}
                    <div className="relative mb-4 max-w-md">
                        <div className="relative z-30">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onKeyDown={handleSearchKeyDown}
                                onFocus={() => searchResults.length > 0 && setShowSearchDropdown(true)}
                                placeholder="Search by item or group name..."
                                className="w-full pl-9 pr-8 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            {searchQuery && (
                                <button
                                    onClick={clearSearch}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        {showSearchDropdown && searchResults.length > 0 && (
                            <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto tiny-scrollbar">
                                {searchResults.map((entry, idx) => (
                                    <button
                                        key={`${entry.type}-${entry.label}-${idx}`}
                                        onClick={() => handleSelectSearchResult(entry)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between gap-2 border-b last:border-b-0"
                                    >
                                        <span className={entry.type === "group" ? "font-semibold text-gray-700" : "text-gray-600"}>
                                            {entry.label}
                                        </span>
                                        <span className="text-xs shrink-0">
                                            {entry.type === "group" ? (
                                                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">Group</span>
                                            ) : (
                                                <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500">{entry.groupName}</span>
                                            )}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {showSearchDropdown && searchQuery && searchResults.length === 0 && (
                            <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg px-3 py-2 text-sm text-gray-500">
                                No matches found
                            </div>
                        )}
                    </div>

                    {/* Reset inputRefs before rendering all tables */}
                    {(() => { inputRefs.current = []; })()}

                    <div className="mt-3 space-y-6">
                        {Object.keys(groupedData).length > 0 ? (
                            Object.entries(groupedData)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([groupName, items]) => (
                                    <div
                                        key={groupName}
                                        ref={(el) => (groupRefs.current[groupName] = el)}
                                    >
                                        {/* Group heading */}
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="h-5 w-1 bg-blue-500 rounded"></div>
                                            <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                                {groupName}
                                            </div>
                                        </div>

                                        <div className="rounded-md border overflow-auto max-h-[500px] tiny-scrollbar">
                                            <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                                <TableHeader />
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {[...items]
                                                        .sort((a, b) => a.name.localeCompare(b.name))
                                                        .map((item, index) => {
                                                            const showSecondary = shouldShowSecondary(item);

                                                            const missingRate = !item.opening_rate;
                                                            const missingUnitDef = !item.unitdefinition;
                                                            const missingPrimaryUom = !item.primary_unit;
                                                            const missingSecondaryUom = showSecondary && !item.secondary_unit;

                                                            const hasError =
                                                                missingRate ||
                                                                missingUnitDef ||
                                                                missingPrimaryUom ||
                                                                missingSecondaryUom;

                                                            const rowClass = hasError
                                                                ? "bg-yellow-50 hover:bg-yellow-100 border-yellow-300"
                                                                : "hover:bg-gray-100";

                                                            return (
                                                                <tr
                                                                    key={`${item.category}-${item.groupName}-${item.name}-${index}`}
                                                                    ref={(el) => {
                                                                        if (el) itemRefs.current[item._key] = el;
                                                                    }}
                                                                    className={`transition-colors duration-300 ${rowClass}`}
                                                                    title={hasError ? "This item has missing data (rate, unit definition, or UOM)" : undefined}
                                                                >
                                                                    <td className="px-3 py-2">
                                                                        {item.name}
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        {item.unitdefinition?.unit} ({item.unitdefinition?.uom || 'N/A'})
                                                                    </td>
                                                                    <td className={`px-3 py-2 ${missingRate ? "text-yellow-700 font-medium" : ""}`}>
                                                                        {item.opening_rate ? formatSalesValue(item.opening_rate) : "-"}
                                                                    </td>

                                                                    <td className="px-3 py-2 border-r border-gray-200">
                                                                        <input
                                                                            ref={(el) => {
                                                                                if (el) inputRefs.current.push(el);
                                                                            }}
                                                                            type="number"
                                                                            min="0"
                                                                            step="any"
                                                                            value={item.primaryQty}
                                                                            className="w-20 px-2 py-1 border rounded text-center"
                                                                            onChange={(e) => handlePrimaryChange(item._key, e.target.value)}
                                                                            onKeyDown={handleKeyDown}
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        {item.primary_unit || "N/A"}
                                                                    </td>

                                                                    <td className="px-3 py-2 border-r border-gray-200">
                                                                        {showSecondary ? (
                                                                            <input
                                                                                ref={(el) => {
                                                                                    if (el) inputRefs.current.push(el);
                                                                                }}
                                                                                type="number"
                                                                                min="0"
                                                                                step="any"
                                                                                value={item.secondaryQty}
                                                                                className="w-20 px-2 py-1 border rounded text-center"
                                                                                onChange={(e) => handleSecondaryChange(item._key, e.target.value)}
                                                                                onKeyDown={handleKeyDown}
                                                                            />
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        {showSecondary ? (item.secondary_unit || "N/A") : '-'}
                                                                    </td>

                                                                    <td className="px-3 py-2">{formatSalesValue(computeCost(item))}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))
                        ) : (
                            <div className="rounded-md border px-3 py-4 text-center text-gray-500">
                                No data available
                            </div>
                        )}
                    </div>

                    {editableData.length > 0 && (
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                            >
                                {isSaving ? 'Saving...' : 'Save Opening Counts'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default AddOpeningCount;